import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import {
	Layout,
	Menu,
	Button,
	Input,
	Tabs,
	Tooltip,
	Modal,
	message,
	Checkbox,
	Spin,
} from 'antd';
import {
	PlusOutlined,
	DeleteOutlined,
	ExclamationCircleOutlined,
	SaveOutlined,
} from '@ant-design/icons';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { queryDepartmentsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/departments/queryDepartmentsByCompanyIdGraphql.js';
import { querySubCompanyByCompanyIdIndex } from 'src/_shared/api/graphql/custom/sub-company/subCompanyByIdGraphql.js';
import { queryUserGroupsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/userGroups/queryUserGroupsByCompanyIdGraphql.js';
import { queryUsersByCompanyIdIndex } from 'src/settings-dashboard/custom/users/getUsersByCompanyIdGraphql.js';
import {
	CreateFeatureFlag,
	DeleteFeatureFlag,
	GetFeatureFlagsByCompanyId,
	UpdateFeatureFlag,
} from 'src/_shared/api/graphql/custom/company/featureFlagQueriesMutationsGraphql.js';

const { Header, Sider, Content } = Layout;

const UserRole = {
	admin: 'admin',
	employee: 'employee',
	extendedUser: 'extendedUser',
	manager: 'manager',
	superAdmin: 'superAdmin',
	superUser: 'superUser',
	supportAdmin: 'supportAdmin',
};

const ROW_HEIGHT = 36;

const VirtualizedRow = React.memo(({ index, style, data: rowData }) => {
	const { rows, selectedSet, onToggle, onRemove, removeOnly, entityType } = rowData;
	const row = rows[index];
	const isSelected = selectedSet.has(row.id);

	return (
		<div
			style={{
				...style,
				display: 'flex',
				alignItems: 'center',
				borderBottom: '1px solid #f0f0f0',
				padding: '0 8px',
				backgroundColor: row.isManual
					? '#fffbe6'
					: index % 2 === 0
						? '#fff'
						: '#fafafa',
			}}
		>
			{removeOnly ? (
				<Button
					type="text"
					danger
					icon={<DeleteOutlined />}
					size="small"
					onClick={() => onRemove(row.id)}
					style={{ marginRight: 4, flexShrink: 0 }}
				/>
			) : (
				<Checkbox
					checked={isSelected}
					onChange={() => onToggle(row.id)}
					style={{ marginRight: 8 }}
				/>
			)}
			<span
				style={{
					flex: entityType === 'role' ? 1 : '0 0 280px',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					fontSize: 13,
				}}
				title={row.id}
			>
				{row.id}
			</span>
			{entityType !== 'role' && (
				<>
					<span
						style={{
							flex: 1,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							fontSize: 13,
							paddingLeft: 8,
						}}
					>
						{row.displayName}
					</span>
					<span
						style={{
							flex: '0 0 120px',
							fontSize: 12,
							color: '#595959',
							paddingLeft: 8,
						}}
					>
						{row.info}
					</span>
				</>
			)}
		</div>
	);
});

const EntityTable = ({
	entities,
	selectedIds,
	onSelectionChange,
	entityType,
	loading = false,
	onAddCustomId = null,
	removeOnly = false,
}) => {
	const [customId, setCustomId] = useState('');
	const [filter, setFilter] = useState('');
	const listContainerRef = useRef(null);
	const [listHeight, setListHeight] = useState(400);

	useEffect(() => {
		const container = listContainerRef.current;
		if (!container) return;

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const available = entry.contentRect.height;
				if (available > 0) {
					setListHeight(available);
				}
			}
		});

		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	const rows = useMemo(() => {
		const getDisplayName = (entity) => {
			switch (entityType) {
				case 'user':
					return (
						`${entity.firstName || ''} ${entity.lastName || ''}`.trim() ||
						entity.emailAddress ||
						'Unknown'
					);
				case 'department':
				case 'subCompany':
				case 'userGroup':
					return entity.name || 'Unknown';
				case 'role':
					return entity;
				case 'accountClaim':
					return 'Custom ID';
				default:
					return entity.name || entity.id || 'Unknown';
			}
		};

		const getSecondaryInfo = (entity) => {
			switch (entityType) {
				case 'user':
					return entity.emailAddress || '';
				case 'department':
				case 'userGroup':
					return entity.active ? 'Active' : 'Inactive';
				case 'accountClaim':
					return 'Manually added';
				default:
					return '';
			}
		};

		let data = entities.map((entity) => {
			if (entityType === 'role') {
				return { id: entity, displayName: entity, info: '' };
			}
			return {
				id: entity.id,
				displayName: getDisplayName(entity),
				info: getSecondaryInfo(entity),
			};
		});

		const entityIds = new Set(
			entities.map((e) => (entityType === 'role' ? e : e.id))
		);
		const manualData = selectedIds
			.filter((id) => !entityIds.has(id))
			.map((id) => ({
				id,
				displayName: 'Custom ID',
				info: 'Manually added',
				isManual: true,
			}));

		data = [...data, ...manualData];

		if (filter) {
			const lowerFilter = filter.toLowerCase();
			data = data.filter(
				(row) =>
					row.id.toLowerCase().includes(lowerFilter) ||
					row.displayName.toLowerCase().includes(lowerFilter)
			);
		}

		return data;
	}, [entities, selectedIds, entityType, filter]);

	const onToggle = useCallback(
		(id) => {
			const newSelected = selectedSet.has(id)
				? selectedIds.filter((sid) => sid !== id)
				: [...selectedIds, id];
			onSelectionChange(newSelected);
		},
		[selectedIds, selectedSet, onSelectionChange]
	);

	const onRemove = useCallback(
		(id) => {
			onSelectionChange(selectedIds.filter((sid) => sid !== id));
		},
		[selectedIds, onSelectionChange]
	);

	const allVisibleSelected =
		rows.length > 0 && rows.every((row) => selectedSet.has(row.id));

	const onToggleAll = useCallback(() => {
		if (allVisibleSelected) {
			const visibleIds = new Set(rows.map((r) => r.id));
			onSelectionChange(selectedIds.filter((id) => !visibleIds.has(id)));
		} else {
			const newIds = new Set(selectedIds);
			for (const row of rows) {
				newIds.add(row.id);
			}
			onSelectionChange([...newIds]);
		}
	}, [rows, selectedIds, allVisibleSelected, onSelectionChange]);

	const handleAddCustomId = () => {
		if (customId.trim() && onAddCustomId) {
			onAddCustomId(customId.trim());
			setCustomId('');
		}
	};

	const itemData = useMemo(
		() => ({ rows, selectedSet, onToggle, onRemove, removeOnly, entityType }),
		[rows, selectedSet, onToggle, onRemove, removeOnly, entityType]
	);

	if (loading) {
		return (
			<div style={{ textAlign: 'center', padding: 40 }}>
				<Spin />
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			{entityType !== 'role' && onAddCustomId && (
				<div style={{ marginBottom: 16, display: 'flex', flexShrink: 0 }}>
					<Input
						placeholder={`Enter ${entityType} ID`}
						value={customId}
						onChange={(e) => setCustomId(e.target.value)}
						style={{ maxWidth: 300, marginRight: 8 }}
						onPressEnter={handleAddCustomId}
					/>
					<Button
						type="primary"
						onClick={handleAddCustomId}
						disabled={!customId.trim()}
					>
						Add
					</Button>
				</div>
			)}
			<div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
				<Input
					placeholder="Filter..."
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
					allowClear
					style={{ maxWidth: 300 }}
				/>
				<span style={{ fontSize: 12, color: '#595959' }}>
					{rows.length} items ({selectedIds.length} selected)
				</span>
			</div>
			<div
				style={{
					border: '1px solid #f0f0f0',
					borderRadius: 4,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					minHeight: 0,
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						padding: '8px 8px',
						backgroundColor: '#fafafa',
						borderBottom: '1px solid #f0f0f0',
						fontWeight: 600,
						fontSize: 13,
						flexShrink: 0,
					}}
				>
					{removeOnly ? (
						<span style={{ width: 32, flexShrink: 0 }} />
					) : (
						<Checkbox
							checked={allVisibleSelected}
							indeterminate={
								!allVisibleSelected &&
								rows.some((row) => selectedSet.has(row.id))
							}
							onChange={onToggleAll}
							style={{ marginRight: 8 }}
						/>
					)}
					<span style={{ flex: entityType === 'role' ? 1 : '0 0 280px' }}>
						{entityType === 'role' ? 'Role' : 'ID'}
					</span>
					{entityType !== 'role' && (
						<>
							<span style={{ flex: 1, paddingLeft: 8 }}>
								{entityType === 'user' ? 'Name/Email' : 'Name'}
							</span>
							<span style={{ flex: '0 0 120px', paddingLeft: 8 }}>Info</span>
						</>
					)}
				</div>
				<div ref={listContainerRef} style={{ flex: 1, minHeight: 0 }}>
					<List
						height={Math.min(listHeight, rows.length * ROW_HEIGHT)}
						itemCount={rows.length}
						itemSize={ROW_HEIGHT}
						itemData={itemData}
						width="100%"
						overscanCount={20}
					>
						{VirtualizedRow}
					</List>
				</div>
			</div>
		</div>
	);
};

const FeatureFlagManager = (props) => {
	const { client, currentUser } = props;

	const companyId = currentUser?.companyId || currentUser?.company?.id;

	const [featureFlags, setFeatureFlags] = useState([]);
	const [selectedFlagIndex, setSelectedFlagIndex] = useState(null);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [newFlagCounter, setNewFlagCounter] = useState(1);

	const [departments, setDepartments] = useState([]);
	const [subCompanies, setSubCompanies] = useState([]);
	const [userGroups, setUserGroups] = useState([]);
	const [users, setUsers] = useState([]);
	const [roles] = useState(Object.values(UserRole));

	const [loading, setLoading] = useState(false);
	const [entityLoading, setEntityLoading] = useState({
		flags: false,
		departments: false,
		subCompanies: false,
		userGroups: false,
		users: false,
	});

	const [nextTokens, setNextTokens] = useState({
		flags: null,
		departments: null,
		subCompanies: null,
		userGroups: null,
		users: null,
	});

	useEffect(() => {
		if (companyId) {
			setSelectedFlagIndex(null);
			setHasUnsavedChanges(false);
			fetchFeatureFlags();
		}
	}, [companyId]);

	const fetchCompanyGroupData = async () => {
		setEntityLoading((prev) => ({
			...prev,
			departments: true,
			subCompanies: true,
			userGroups: true,
			users: true,
		}));

		try {
			const { data: departmentsData } = await client.query({
				query: gql(queryDepartmentsByCompanyIdIndex),
				variables: {
					companyId,
					limit: 1000,
					nextToken: null,
				},
			});

			const { data: subCompaniesData } = await client.query({
				query: gql(querySubCompanyByCompanyIdIndex),
				variables: {
					companyId,
					limit: 1000,
					nextToken: null,
				},
			});

			const { data: userGroupsData } = await client.query({
				query: gql(queryUserGroupsByCompanyIdIndex),
				variables: {
					companyId,
					limit: 1000,
					nextToken: null,
				},
			});

			try {
				const { data: usersData } = await client.query({
					query: queryUsersByCompanyIdIndex,
					variables: {
						companyId,
						limit: 1000,
						nextToken: null,
					},
				});

				setUsers(usersData.queryUsersByCompanyIdIndex.items);
				setNextTokens({
					...nextTokens,
					users: usersData.queryUsersByCompanyIdIndex.nextToken,
				});
			} catch {
				setUsers([]);
				setNextTokens({
					...nextTokens,
					users: null,
				});
			}

			setDepartments(departmentsData.queryDepartmentsByCompanyIdIndex.items);
			setSubCompanies(subCompaniesData.querySubCompanyByCompanyIdIndex.items);
			setUserGroups(userGroupsData.queryUserGroupsByCompanyIdIndex.items);

			setNextTokens({
				...nextTokens,
				departments: departmentsData.queryDepartmentsByCompanyIdIndex.nextToken,
				subCompanies:
					subCompaniesData.querySubCompanyByCompanyIdIndex.nextToken,
				userGroups: userGroupsData.queryUserGroupsByCompanyIdIndex.nextToken,
			});
		} catch (error) {
			message.error('Failed to fetch entity data');
			console.error(error);
		} finally {
			setEntityLoading((prev) => ({
				...prev,
				departments: false,
				subCompanies: false,
				userGroups: false,
				users: false,
			}));
		}
	};

	const fetchFeatureFlags = async () => {
		setEntityLoading((prev) => ({ ...prev, flags: true }));

		try {
			const response = await client.query({
				query: GetFeatureFlagsByCompanyId,
				variables: {
					companyId: companyId,
					limit: 50,
					nextToken: null,
				},
				fetchPolicy: 'network-only',
			});

			setFeatureFlags(response?.data?.getFeatureFlagsByCompanyId?.items || []);
			setNextTokens((prev) => ({
				...prev,
				flags: response?.data?.getFeatureFlagsByCompanyId?.nextToken || null,
			}));

			await fetchCompanyGroupData();
		} catch (error) {
			message.error('Failed to fetch feature flags');
			console.error(error);
		} finally {
			setEntityLoading((prev) => ({ ...prev, flags: false }));
		}
	};

	const addNewFeatureFlag = () => {
		const newFlag = {
			companyId,
			featureFlagName: `newFeatureFlag${newFlagCounter}`,
			byUserId: [],
			byRole: [],
			byDepartmentId: [],
			bySubCompanyId: [],
			byUserGroupId: [],
			byAccountClaimId: [],
			isNew: true,
		};

		setFeatureFlags([...featureFlags, newFlag]);
		setNewFlagCounter(newFlagCounter + 1);
		setSelectedFlagIndex(featureFlags.length);
		setHasUnsavedChanges(true);
	};

	const handleDeleteFlag = (index) => {
		const flagToDelete = featureFlags[index];

		if (!flagToDelete.isNew) {
			Modal.confirm({
				title: `Are you sure you want to delete ${flagToDelete.featureFlagName}?`,
				content: 'This cannot be undone.',
				okText: 'Yes, delete it',
				okType: 'danger',
				cancelText: 'Cancel',
				onOk: async () => {
					try {
						await client.mutate({
							mutation: DeleteFeatureFlag,
							variables: {
								companyId,
								featureFlagName: flagToDelete.featureFlagName,
							},
							refetchQueries: [
								{
									query: GetFeatureFlagsByCompanyId,
									variables: {
										companyId: companyId,
										limit: 50,
										nextToken: null,
									},
								},
							],
							awaitRefetchQueries: true,
						});

						const updatedFlags = [...featureFlags];
						updatedFlags.splice(index, 1);
						setFeatureFlags(updatedFlags);

						if (selectedFlagIndex === index) {
							setSelectedFlagIndex(null);
						} else if (selectedFlagIndex > index) {
							setSelectedFlagIndex(selectedFlagIndex - 1);
						}

						message.success(
							`Feature flag "${flagToDelete.featureFlagName}" deleted`
						);
					} catch (error) {
						message.error('Failed to delete feature flag');
						console.error(error);
					}
				},
			});
		} else {
			const updatedFlags = [...featureFlags];
			updatedFlags.splice(index, 1);
			setFeatureFlags(updatedFlags);

			if (selectedFlagIndex === index) {
				setSelectedFlagIndex(null);
			} else if (selectedFlagIndex > index) {
				setSelectedFlagIndex(selectedFlagIndex - 1);
			}
		}
	};

	const handleNameChange = (e) => {
		if (selectedFlagIndex === null) return;

		const updatedFlags = [...featureFlags];
		updatedFlags[selectedFlagIndex] = {
			...updatedFlags[selectedFlagIndex],
			featureFlagName: e.target.value,
		};

		setFeatureFlags(updatedFlags);
		setHasUnsavedChanges(true);
	};

	const saveFeatureFlag = async () => {
		if (selectedFlagIndex === null) return;

		const currentFlag = featureFlags[selectedFlagIndex];
		setLoading(true);

		try {
			if (currentFlag.isNew) {
				const input = {
					companyId,
					featureFlagName: currentFlag.featureFlagName,
					byUserId: currentFlag.byUserId,
					byRole: currentFlag.byRole,
					byDepartmentId: currentFlag.byDepartmentId,
					bySubCompanyId: currentFlag.bySubCompanyId,
					byUserGroupId: currentFlag.byUserGroupId,
					byAccountClaimId: currentFlag.byAccountClaimId,
				};

				const response = await client.mutate({
					mutation: CreateFeatureFlag,
					variables: { input },
					refetchQueries: [
						{
							query: GetFeatureFlagsByCompanyId,
							variables: {
								companyId: companyId,
								limit: 50,
								nextToken: null,
							},
						},
					],
					awaitRefetchQueries: true,
				});

				const updatedFlags = [...featureFlags];
				updatedFlags[selectedFlagIndex] = {
					...response.data.createFeatureFlag,
					isNew: false,
				};

				setFeatureFlags(updatedFlags);
				message.success(
					`Feature flag "${currentFlag.featureFlagName}" created`
				);
			} else {
				const input = {
					companyId,
					featureFlagName: currentFlag.featureFlagName,
					byUserId: currentFlag.byUserId,
					byRole: currentFlag.byRole,
					byDepartmentId: currentFlag.byDepartmentId,
					bySubCompanyId: currentFlag.bySubCompanyId,
					byUserGroupId: currentFlag.byUserGroupId,
					byAccountClaimId: currentFlag.byAccountClaimId,
				};

				const response = await client.mutate({
					mutation: UpdateFeatureFlag,
					variables: { input },
					refetchQueries: [
						{
							query: GetFeatureFlagsByCompanyId,
							variables: {
								companyId: companyId,
								limit: 50,
								nextToken: null,
							},
						},
					],
					awaitRefetchQueries: true,
				});

				const updatedFlags = [...featureFlags];
				updatedFlags[selectedFlagIndex] = {
					...response.data.updateFeatureFlag,
					isNew: false,
				};

				setFeatureFlags(updatedFlags);
				message.success(
					`Feature flag "${currentFlag.featureFlagName}" updated`
				);
			}

			setHasUnsavedChanges(false);
		} catch (error) {
			message.error('Failed to save feature flag');
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleEntitySelection = (group, selectedIds) => {
		if (selectedFlagIndex === null) return;

		const updatedFlags = [...featureFlags];
		updatedFlags[selectedFlagIndex] = {
			...updatedFlags[selectedFlagIndex],
			[group]: selectedIds,
		};

		setFeatureFlags(updatedFlags);
		setHasUnsavedChanges(true);
	};

	const handleAddCustomId = (group, id) => {
		if (selectedFlagIndex === null || !id) return;

		const updatedFlags = [...featureFlags];
		const currentIds = updatedFlags[selectedFlagIndex][group] || [];

		if (!currentIds.includes(id)) {
			updatedFlags[selectedFlagIndex] = {
				...updatedFlags[selectedFlagIndex],
				[group]: [...currentIds, id],
			};

			setFeatureFlags(updatedFlags);
			setHasUnsavedChanges(true);
		}
	};

	const menuItems = featureFlags.map((flag, index) => ({
		key: index.toString(),
		label: (
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
				}}
			>
				<span>{flag.featureFlagName}</span>
				<Button
					type="text"
					danger
					icon={<DeleteOutlined />}
					onClick={(e) => {
						e.stopPropagation();
						handleDeleteFlag(index);
					}}
				/>
			</div>
		),
		onClick: () => setSelectedFlagIndex(index),
	}));

	const tabItems = [
		{
			key: 'byRole',
			label: 'By Role',
			children: (
				<EntityTable
					entities={roles}
					selectedIds={
						selectedFlagIndex !== null
							? featureFlags[selectedFlagIndex].byRole || []
							: []
					}
					onSelectionChange={(selectedRowKeys) =>
						handleEntitySelection('byRole', selectedRowKeys)
					}
					entityType="role"
					loading={entityLoading.flags}
				/>
			),
		},
		{
			key: 'byUserId',
			label: 'By User ID',
			children: (
				<EntityTable
					entities={users}
					selectedIds={
						selectedFlagIndex !== null
							? featureFlags[selectedFlagIndex].byUserId || []
							: []
					}
					onSelectionChange={(selectedRowKeys) =>
						handleEntitySelection('byUserId', selectedRowKeys)
					}
					entityType="user"
					loading={entityLoading.users}
					removeOnly
					onAddCustomId={(id) => handleAddCustomId('byUserId', id)}
				/>
			),
		},
		{
			key: 'byDepartmentId',
			label: 'By Department ID',
			children: (
				<EntityTable
					entities={departments}
					selectedIds={
						selectedFlagIndex !== null
							? featureFlags[selectedFlagIndex].byDepartmentId || []
							: []
					}
					onSelectionChange={(selectedRowKeys) =>
						handleEntitySelection('byDepartmentId', selectedRowKeys)
					}
					entityType="department"
					loading={entityLoading.departments}

					onAddCustomId={(id) => handleAddCustomId('byDepartmentId', id)}
				/>
			),
		},
		{
			key: 'bySubCompanyId',
			label: 'By Sub Company ID',
			children: (
				<EntityTable
					entities={subCompanies}
					selectedIds={
						selectedFlagIndex !== null
							? featureFlags[selectedFlagIndex].bySubCompanyId || []
							: []
					}
					onSelectionChange={(selectedRowKeys) =>
						handleEntitySelection('bySubCompanyId', selectedRowKeys)
					}
					entityType="subCompany"
					loading={entityLoading.subCompanies}

					onAddCustomId={(id) => handleAddCustomId('bySubCompanyId', id)}
				/>
			),
		},
		{
			key: 'byUserGroupId',
			label: 'By User Group ID',
			children: (
				<EntityTable
					entities={userGroups}
					selectedIds={
						selectedFlagIndex !== null
							? featureFlags[selectedFlagIndex].byUserGroupId || []
							: []
					}
					onSelectionChange={(selectedRowKeys) =>
						handleEntitySelection('byUserGroupId', selectedRowKeys)
					}
					entityType="userGroup"
					loading={entityLoading.userGroups}

					onAddCustomId={(id) => handleAddCustomId('byUserGroupId', id)}
				/>
			),
		},
		{
			key: 'byAccountClaimId',
			label: 'By Account Claim ID',
			children: (
				<EntityTable
					entities={[]}
					selectedIds={
						selectedFlagIndex !== null
							? featureFlags[selectedFlagIndex].byAccountClaimId || []
							: []
					}
					onSelectionChange={(selectedRowKeys) =>
						handleEntitySelection('byAccountClaimId', selectedRowKeys)
					}
					entityType="accountClaim"
					loading={false}
					removeOnly
					onAddCustomId={(id) => handleAddCustomId('byAccountClaimId', id)}
				/>
			),
		},
	];

	return (
		<Layout style={{ height: '100vh' }}>
			<style>{`
				.feature-flag-tabs-fill {
					flex: 1;
					display: flex;
					flex-direction: column;
					min-height: 0;
				}
				.feature-flag-tabs-fill > .ant-tabs-content-holder {
					flex: 1;
					min-height: 0;
					display: flex;
				}
				.feature-flag-tabs-fill > .ant-tabs-content-holder > .ant-tabs-content {
					flex: 1;
					min-height: 0;
				}
				.feature-flag-tabs-fill > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane-active {
					height: 100%;
				}
			`}</style>
			<Sider width={300} theme="light" className="feature-flag-sider">
				<div style={{ padding: '16px' }}>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						block
						onClick={addNewFeatureFlag}
					>
						Add Feature Flag
					</Button>
				</div>
				<Menu
					mode="inline"
					selectedKeys={
						selectedFlagIndex !== null ? [selectedFlagIndex.toString()] : []
					}
					style={{ height: 'calc(100% - 64px)', overflow: 'auto' }}
					items={menuItems}
					className="feature-flag-menu"
				/>
			</Sider>
			<Layout>
				<Header
					style={{
						background: '#fff',
						padding: '0 16px',
						display: 'flex',
						alignItems: 'center',
					}}
				>
					{selectedFlagIndex !== null && (
						<>
							<Input
								placeholder="Feature Flag Name"
								value={featureFlags[selectedFlagIndex].featureFlagName}
								onChange={handleNameChange}
								style={{ width: 300, marginRight: 16 }}
							/>
							<Button
								type="primary"
								icon={<SaveOutlined />}
								onClick={saveFeatureFlag}
								loading={loading}
							>
								Save
							</Button>
							{hasUnsavedChanges && (
								<Tooltip title="You have unsaved changes!">
									<ExclamationCircleOutlined
										style={{ color: '#faad14', marginLeft: 8 }}
									/>
								</Tooltip>
							)}
						</>
					)}
				</Header>
				<Content
					style={{
						margin: '24px',
						background: '#fff',
						padding: 24,
						minHeight: 280,
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
					}}
				>
					{selectedFlagIndex !== null ? (
						<Tabs
							defaultActiveKey="byRole"
							items={tabItems}
							style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
							className="feature-flag-tabs-fill"
						/>
					) : (
						<div style={{ textAlign: 'center' }}>
							<p>Select a feature flag from the sidebar or create a new one</p>
						</div>
					)}
				</Content>
			</Layout>
		</Layout>
	);
};

export default withApollo(FeatureFlagManager);
